/**
 * WebSocket-backed `SnapshotSource` for the WP6 renderer.
 *
 * Contract: WP6's `mountRenderer` takes a `SnapshotSource` with
 * `subscribe(cb) → { dispose }`. WP7's job is to route WS `snapshot`
 * messages into that callback (converting the `WireSimState` + tick
 * into WP6's `SnapshotEntry` shape).
 *
 * Design:
 *   • The source is a pure fan-out — one subscriber at a time is
 *     enough (the renderer). Extra subscribers would need a subject.
 *   • Snapshots that arrive BEFORE `subscribe()` are buffered so the
 *     renderer catches up on the first frame (dev HMR churn, mount
 *     ordering).
 *
 * The class itself doesn't know about WebSocket — it just exposes a
 * `pushSnapshot(entry)` for the socket-message handler and a
 * `subscribe(onSnapshot)` for WP6. Wiring lives in the store bootstrap.
 */

import type { SnapshotEntry } from "./snapshots.js";
import type { SnapshotSource, SnapshotSourceHandle } from "../render/index.js";
import type { ServerMessage, WireSimState } from "@pit/protocol";

/**
 * Pushable, subscribable snapshot channel. WP6 sees the read half;
 * the WS-message handler writes via `push`.
 */
export class WsSnapshotSource implements SnapshotSource {
  // Fan-out to ALL live subscribers. Previously this was a single-slot
  // "last-writer-wins" subscriber, which broke under React StrictMode's
  // mount → cleanup → mount cycle: two async `mountRenderer` calls race to
  // `subscribe()`, and whichever resolved LAST won the slot — not
  // necessarily the renderer that stayed mounted. The orphaned (still-
  // visible) renderer then received only the pre-subscribe drain and no
  // further pushes, so its snapshot buffer froze at the first tick while
  // the HUD (which reads `latestSnapshot()` directly) kept updating.
  // A Set makes subscribe/dispose order-independent and StrictMode-safe.
  private readonly subscribers = new Set<(e: SnapshotEntry) => void>();
  private buffered: SnapshotEntry[] = [];
  private lastEntry: SnapshotEntry | null = null;

  /**
   * Push a snapshot from the socket layer. If nobody's subscribed
   * yet, buffer up to `RING_CAPACITY` frames; the buffer is drained
   * on subscribe. Also cached separately for HUD sampler polling.
   */
  push(entry: SnapshotEntry): void {
    this.lastEntry = entry;
    if (this.subscribers.size > 0) {
      for (const s of this.subscribers) s(entry);
      return;
    }
    this.buffered.push(entry);
    // Cap the pre-subscribe buffer so a slow mount doesn't accumulate
    // unbounded frames. 64 mirrors WP6's ring capacity — more than
    // enough for ~2s at 30 Hz.
    if (this.buffered.length > 64) {
      this.buffered.shift();
    }
  }

  /**
   * WP6-facing subscribe. Fan-out: every live subscriber receives every
   * push. A new subscriber first drains any pre-subscribe buffer so a
   * late-mounting renderer catches up on the first frame. Dispose is
   * scoped to the exact callback registered, so a torn-down renderer
   * can never detach a still-live one.
   */
  subscribe(onSnapshot: (entry: SnapshotEntry) => void): SnapshotSourceHandle {
    this.subscribers.add(onSnapshot);
    // Drain any pre-subscribe frames in order into THIS subscriber only.
    const pending = this.buffered;
    this.buffered = [];
    for (const e of pending) onSnapshot(e);
    return {
      dispose: (): void => {
        this.subscribers.delete(onSnapshot);
      },
    };
  }

  /** For tests. */
  peekBuffered(): readonly SnapshotEntry[] {
    return this.buffered.slice();
  }

  /**
   * Latest WireSimState pushed through the source (or null). Used by
   * the HUD sampler to read snapshots into React state at 10 Hz
   * without competing with WP6's ring buffer subscriber slot.
   */
  latestSnapshot(): WireSimState | null {
    return this.lastEntry === null ? null : this.lastEntry.state;
  }
}

/**
 * Handle a parsed `snapshot` server message: adapt to a `SnapshotEntry`
 * and push. Returns true if the message was of the snapshot type and
 * we forwarded it. This helper is deliberately narrow so the socket
 * message router can call it without touching internals.
 */
export function handleSnapshotMessage(
  source: WsSnapshotSource,
  msg: ServerMessage,
): boolean {
  if (msg.type !== "snapshot") return false;
  source.push({ tick: msg.tick, state: msg.state });
  return true;
}
